import {
  Col,
  DatePicker,
  Input,
  Row,
  Table,
  Button,
  Select,
  notification,
  Modal,
  Tag,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import { AssesmentUrl, BaseUrl } from "../Constants/Constants";

const { RangePicker } = DatePicker;
const { Option } = Select;

const CareerDevelopmentReport = () => {
  const [api, contextHolder] = notification.useNotification();

  // CORE STATE
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // UI STATE
  const [searchEmployee, setSearchEmployee] = useState("");
  const [dateRange, setDateRange] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [allSelectedRecords, setAllSelectedRecords] = useState([]);

  const EMPTY_FILTERS = {
    dateRange: null,
    group_name: null,
    passing_status: null,
    assessment: null,
  };

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);

  const [groups, setGroups] = useState([]);

  // reset the draft filters when modal opens
  useEffect(() => {
    if (isFilterOpen) {
      setDraftFilters(filters);
    }
  }, [isFilterOpen, filters]);

  useEffect(() => {
    const loadGroups = async () => {
      try {
        const res = await axios.get(`${BaseUrl}career-development/getGroups`, {
          withCredentials: true,
        });
        setGroups(res.data || []);
      } catch (e) {
        console.error("Failed to load groups", e);
      }
    };
    loadGroups();
  }, []);

  // FETCH
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await axios.get(
          `${AssesmentUrl}get_group_assessment_pass_status`,
          {
            withCredentials: true,
          },
        );

        if (!Array.isArray(res.data?.assessments)) {
          throw new Error("Invalid API response");
        }

        setRawData(res.data.assessments ?? []);
      } catch (e) {
        const msg = e?.message || "Failed to load data";
        setError(msg);

        api.error({
          message: "Error",
          description: msg,
        });

        setRawData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api]);

  // FILTERED + TRANSFORMED DATA
  const tableData = useMemo(() => {
    if (!Array.isArray(rawData)) return [];

    let filtered = rawData.map((d, index) => ({
      key: index + 1,
      employeeId: d.employee_id ?? "N/A",
      email: d.email ?? "N/A", // You'll need actual email from API or remove this column
      username: d.user_name ?? "N/A",
      groupname: d.group_name ?? "N/A",
      assessment: d.assessment_name ?? "N/A",
      score: `${d.score ?? 0}/${d.assessment_marks ?? 0}`,
      passingStatus: d.passing_status ?? "N/A",
      status: d.is_completed === "completed" ? "Completed" : "Pending",
      attemptCount: d.assessment_attempt_count ?? 0,
    }));

    // Filter by employee search
    if (searchEmployee) {
      filtered = filtered.filter((row) =>
        row.username.toLowerCase().includes(searchEmployee.toLowerCase()),
      );
    }

    if (filters.dateRange) {
      const [start, end] = filters.dateRange;
      filtered = filtered.filter((row) => {
        const assessmentDate = moment(row.created_at);
        return assessmentDate.isBetween(start, end, "day", "[]");
      });
    }

    // Apply group filter
    if (filters.group_name) {
      filtered = filtered.filter((row) => row.groupname === filters.group_name);
    }

    // Apply status filter
    if (filters.passing_status) {
      filtered = filtered.filter(
        (row) => row.passingStatus === filters.passing_status,
      );
    }

    if (filters.assessment) {
      filtered = filtered.filter(
        (row) =>
          row.assessment.toLowerCase() === filters.assessment.toLowerCase(),
      );
    }
    // Restore checkboxes for previously selected items
    const keysInCurrentView = filtered.map((item) => item.key);
    const selectedKeysStillVisible = allSelectedRecords
      .filter((record) => keysInCurrentView.includes(record.key))
      .map((record) => record.key);

    setSelectedRowKeys(selectedKeysStillVisible);

    return filtered;

    return filtered;
  }, [rawData, searchEmployee, filters, dateRange]);

  const allSelected =
    !loading &&
    tableData.length > 0 &&
    tableData.every((row) => selectedRowKeys.includes(row.key));

  const isFilterActive = Boolean(
    filters.group_name ||
    filters.passing_status ||
    filters.dateRange ||
    filters.assessment,
  );

  const handleSelectedRowToDownload = () => {
    const allCurrentKeys = tableData.map((row) => row.key);

    if (allSelected) {
      // Deselect all in current view
      const keysToRemove = new Set(allCurrentKeys);
      const remaining = allSelectedRecords.filter(
        (r) => !keysToRemove.has(r.key),
      );
      setAllSelectedRecords(remaining);
      setSelectedRowKeys([]);
    } else {
      // Select all in current view
      const previousRecords = allSelectedRecords.filter(
        (r) => !tableData.some((item) => item.key === r.key),
      );
      setAllSelectedRecords([...previousRecords, ...tableData]);
      setSelectedRowKeys(allCurrentKeys);
    }
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);

    // Get full data for newly selected keys
    const currentlySelected = tableData.filter((item) =>
      newSelectedRowKeys.includes(item.key),
    );

    // Keep previously selected records that are NOT in current table
    const previouslySelected = allSelectedRecords.filter(
      (record) => !tableData.some((item) => item.key === record.key),
    );

    // Merge: previous + current
    setAllSelectedRecords([...previouslySelected, ...currentlySelected]);
  };

  // DOWNLOAD
  const handleDownload = () => {
    // Determine what to download
    const dataToDownload =
      allSelectedRecords.length > 0
        ? allSelectedRecords // ✅ Use stored records
        : tableData; // No selections - use current table

    if (!dataToDownload.length) {
      api.warning({ message: "No data to download" });
      return;
    }

    // ✅ KEEP ALL THIS CODE BELOW (the CSV generation logic)
    // Create CSV with headers
    const headers = columns.map((col) => col.title).join(",");
    const rows = dataToDownload.map((r) =>
      columns.map((col) => r[col.dataIndex]).join(","),
    );

    const csv = [headers, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Assessment_Report_${Date.now()}.csv`;
    link.click();

    api.success({ message: "Download successful" });
  };

  // TABLE COLUMNS
  const columns = [
    { title: "Employee ID", dataIndex: "employeeId" },
    { title: "User Name", dataIndex: "username" },
    { title: "Email", dataIndex: "email" },
    { title: "Course Name", dataIndex: "groupname" },
    { title: "Assessment Name", dataIndex: "assessment" },
    { title: "Score", dataIndex: "score" },
    { title: "Passing Status", dataIndex: "passingStatus" },
    { title: "Attempts", dataIndex: "attemptCount" },
  ];

  return (
    <div className="scoresMainDiv">
      {contextHolder}

      <h2>Assessment Results Report</h2>

      <Row gutter={16} justify="end" style={{ marginBottom: 12 }}>
        <Col span={2}>
          <Button
            type={isFilterActive ? "primary" : "default"}
            onClick={() => setIsFilterOpen(true)}
          >
            Filter
          </Button>
        </Col>
        <Col span={6}>
          <Input
            placeholder="Search by employee Name"
            value={searchEmployee}
            onChange={(e) => setSearchEmployee(e.target.value)}
            allowClear
          />
        </Col>
        <Col>
          <Button
            onClick={handleSelectedRowToDownload}
            disabled={loading || tableData.length === 0}
            style={{ height: 36 }}
          >
            {loading
              ? "Loading..."
              : allSelected
                ? `DeSelect All (${tableData.length})`
                : `Select All (${tableData.length})`}
          </Button>
        </Col>

        <Col span={4}>
          <Button type="primary" onClick={handleDownload}>
            Download CSV
          </Button>
        </Col>
      </Row>
      {/* This row shows the clarity on selected & persisted data */}
      <Row
        gutter={12}
        align="left"
        justify="start"
        style={{ marginBottom: 10 }}
      >
        {allSelectedRecords.length > 0 && (
          <Col>
            <Button
              danger
              onClick={() => {
                setSelectedRowKeys([]);
                setAllSelectedRecords([]);
              }}
            >
              Clear All ({allSelectedRecords.length})
            </Button>
          </Col>
        )}
        {selectedRowKeys.length > 0 && (
          <Col>
            <Tag color="blue">
              {selectedRowKeys.length} selected
              {allSelectedRecords.length !== selectedRowKeys.length &&
                ` (${allSelectedRecords.length} total)`}
            </Tag>
          </Col>
        )}
      </Row>

      {(filters.group_name ||
        filters.passing_status ||
        filters.dateRange ||
        filters.assessment) && (
        <div style={{ marginBottom: 12 }}>
          {filters.group_name && (
            <Tag
              closable
              onClose={() =>
                setFilters((prev) => ({ ...prev, group_name: null }))
              }
            >
              Group: {filters.group_name}
            </Tag>
          )}

          {filters.passing_status && (
            <Tag
              closable
              onClose={() =>
                setFilters((prev) => ({ ...prev, passing_status: null }))
              }
            >
              Passing Status: {filters.passing_status}
            </Tag>
          )}

          {filters.dateRange && (
            <Tag
              closable
              onClose={() =>
                setFilters((prev) => ({ ...prev, dateRange: null }))
              }
            >
              Date: {moment(filters.dateRange[0]).format("DD MMM")} -{" "}
              {moment(filters.dateRange[1]).format("DD MMM")}
            </Tag>
          )}

          {filters.assessment && (
            <Tag
              closable
              onClose={() =>
                setFilters((prev) => ({ ...prev, assessment: null }))
              }
            >
              Assessment: {filters.assessment}
            </Tag>
          )}

          <Tag
            color="red"
            style={{ cursor: "pointer" }}
            onClick={() => setFilters(EMPTY_FILTERS)}
          >
            Clear All
          </Tag>
        </div>
      )}
      <Table
        loading={loading}
        dataSource={tableData}
        columns={columns}
        rowSelection={{
          selectedRowKeys,
          onChange: onSelectChange,
        }}
        pagination={{
          position: ["bottomCenter"],
          showTotal: (total) => `Total ${total} records`,
        }}
      />

      {/* Filter Modal */}
      <Modal
        title="Filter Assessments"
        open={isFilterOpen}
        onCancel={() => {
          setDraftFilters(filters); // discard changes
          setIsFilterOpen(false);
        }}
        footer={null}
      >
        <Row gutter={[16, 16]}>
          {/* DATE RANGE */}
          <Col span={24}>
            <label>Date Range</label>
            <RangePicker
              style={{ width: "100%" }}
              value={draftFilters.dateRange}
              onChange={(dates) =>
                setDraftFilters({
                  ...draftFilters,
                  dateRange: dates,
                })
              }
            />
          </Col>

          {/* GROUP */}
          <Col span={24}>
            <label>Course</label>
            <Select
              allowClear
              placeholder="Select course"
              style={{ width: "100%" }}
              value={draftFilters.group_name}
              onChange={(value) =>
                setDraftFilters({
                  ...draftFilters,
                  group_name: value,
                })
              }
            >
              {groups.map((g) => (
                <Option key={g.id} value={g.name || g.group_name}>
                  {g.name || g.group_name}
                </Option>
              ))}
            </Select>
          </Col>

          {/* PASSING STATUS */}
          <Col span={24}>
            <label>Passing Status</label>
            <Select
              allowClear
              placeholder="Select passing status"
              style={{ width: "100%" }}
              value={draftFilters.passing_status}
              onChange={(value) =>
                setDraftFilters({
                  ...draftFilters,
                  passing_status: value,
                })
              }
            >
              <Option value="pass">Passed</Option>
              <Option value="fail">Failed</Option>
            </Select>
          </Col>
          {/* ASSESSMENT */}
          <Col span={24}>
            <label>Assessment</label>
            <Select
              allowClear
              placeholder="Select assessment"
              style={{ width: "100%" }}
              value={draftFilters.assessment}
              onChange={(value) =>
                setDraftFilters({
                  ...draftFilters,
                  assessment: value,
                })
              }
            >
              {Array.from(
                new Set(rawData.map((d) => d.assessment_name).filter(Boolean)),
              ).map((assessment) => (
                <Option key={assessment} value={assessment}>
                  {assessment}
                </Option>
              ))}
            </Select>
          </Col>

          {/* ACTIONS */}
          <Col span={24} style={{ textAlign: "right" }}>
            <Button
              style={{ marginRight: 8 }}
              onClick={() => {
                setDraftFilters(EMPTY_FILTERS);
              }}
            >
              Clear
            </Button>

            <Button
              type="primary"
              onClick={() => {
                setFilters(draftFilters);
                setIsFilterOpen(false);
              }}
            >
              Apply
            </Button>
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export default CareerDevelopmentReport;
